import React, { useEffect, useRef, useState } from 'react';
import { AnimatedTextProps } from './index.types';

/**
 * AnimatedText component that applies horizontal scrolling animation
 * when text exceeds the available width.
 *
 * Features:
 * - Automatically detects text overflow
 * - Applies smooth marquee animation for long text
 * - Pauses animation on hover
 * - GPU-accelerated with CSS transforms
 *
 * @remarks
 * Exposes its text once. No `title` — it would hold the same string the element
 * already contains, and a screen reader reads both. The marquee's second copy
 * is `aria-hidden` for the same reason. Truncation is visual only; the DOM
 * always holds the full string.
 */
export const AnimatedText: React.FC<AnimatedTextProps> = ({
  text,
  className = '',
  maxWidth,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const textWidth = textRef.current.scrollWidth;
      setShouldAnimate(textWidth > maxWidth);
    }
  }, [text, maxWidth]);

  return (
    <div className="overflow-hidden" style={{ maxWidth: `${maxWidth}px` }}>
      <div
        ref={textRef}
        className={`${className} whitespace-nowrap ${shouldAnimate ? 'animate-marquee' : ''}`}
      >
        {text}
        {/* The marquee's trailing copy: seen, never announced. */}
        {shouldAnimate && (
          <span className="ml-8" aria-hidden="true">
            {text}
          </span>
        )}
      </div>
    </div>
  );
};
